import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import posthog from "./posthog";
import { getSession, signOut, listGroups, activateGroup, getMyGroup, } from "./auth/AuthGate";
import LoginScreen from "./auth/LoginScreen";
import CodeEntryScreen from "./auth/CodeEntryScreen";
import AdminRegisterPage from "./auth/AdminRegisterPage";
import UserRegisterPage from "./auth/UserRegisterPage";
import SysadminRegisterPage from "./auth/SysadminRegisterPage";
import ForgotPasswordPage from "./auth/ForgotPasswordPage";
import ResetPasswordPage from "./auth/ResetPasswordPage";
import SysadminDashboard from "./admin/SysadminDashboard";
import GroupAdminDashboard from "./admin/GroupAdminDashboard";
export default function App({ onStartGame, onViewGroupMap }) {
    const [screen, setScreen] = useState({ type: "loading" });
    const identifiedUserId = useRef(null);
    function runBootstrap() {
        void bootstrap().catch((err) => {
            console.error("bootstrap failed", err);
            setScreen({ type: "error" });
        });
    }
    useEffect(() => {
        runBootstrap();
    }, []);
    // Back/Forward previously only changed the URL bar (pushState has no listener pairing it
    // with a render) — re-running bootstrap() on popstate makes navigating match reloading that URL.
    useEffect(() => {
        function onPopState() {
            setScreen({ type: "loading" });
            runBootstrap();
        }
        window.addEventListener("popstate", onPopState);
        return () => window.removeEventListener("popstate", onPopState);
    }, []);
    // Unsalted by design (needed to correlate re-registrations under a new account) — this hash
    // removes PII from PostHog at rest, it isn't an anonymization guarantee.
    async function hashEmail(email) {
        const bytes = new TextEncoder().encode(email.trim().toLowerCase());
        const digest = await crypto.subtle.digest("SHA-256", bytes);
        return Array.from(new Uint8Array(digest))
            .map((b) => b.toString(16).padStart(2, "0"))
            .join("")
            .slice(0, 16);
    }
    async function identifyUser(user) {
        if (identifiedUserId.current === user.id)
            return;
        if (identifiedUserId.current)
            posthog.reset();
        const email_hash = await hashEmail(user.email);
        posthog.identify(user.id, {
            email_hash,
            is_sysadmin: user.isSysadmin ?? false,
        });
        identifiedUserId.current = user.id;
    }
    async function bootstrap() {
        const { pathname } = window.location;
        const params = new URLSearchParams(window.location.search);
        const resetToken = params.get("reset-token");
        if (resetToken) {
            setScreen({ type: "reset-password", token: resetToken });
            return;
        }
        const adminToken = params.get("admin-token");
        const inviteCode = params.get("invite") ?? params.get("code");
        const sysadminToken = params.get("sysadmin-token");
        if (adminToken ?? inviteCode ?? sysadminToken) {
            const existingSession = await getSession();
            if (existingSession) {
                await identifyUser(existingSession.user);
                setScreen({
                    type: "already-logged-in",
                    userName: existingSession.user.name,
                });
                return;
            }
            if (adminToken) {
                setScreen({ type: "admin-register", token: adminToken });
                return;
            }
            if (sysadminToken) {
                setScreen({ type: "sysadmin-register", token: sysadminToken });
                return;
            }
            setScreen({ type: "user-register", code: inviteCode });
            return;
        }
        const session = await getSession();
        if (!session) {
            setScreen({ type: "login" });
            return;
        }
        await identifyUser(session.user);
        if (pathname === "/play") {
            await ensureActiveGroup(session.session.activeOrganizationId);
            const myGroup = await getMyGroup();
            if (!myGroup) {
                setScreen({ type: "no-access" });
                return;
            }
            onStartGame(session.user);
            return;
        }
        if (pathname === "/sysadmin") {
            if (!session.user.isSysadmin) {
                window.location.replace("/");
                return;
            }
            setScreen({ type: "sysadmin-dashboard" });
            return;
        }
        if (pathname === "/admin") {
            await ensureActiveGroup(session.session.activeOrganizationId);
            const myGroup = await getMyGroup();
            if (!myGroup || (myGroup.role !== "admin" && myGroup.role !== "owner")) {
                window.location.replace("/");
                return;
            }
            posthog.setPersonProperties({ is_group_admin: true });
            setScreen({ type: "group-admin-dashboard" });
            return;
        }
        // Index: /
        if (session.user.isSysadmin) {
            window.history.pushState({}, "", "/sysadmin");
            setScreen({ type: "sysadmin-dashboard" });
            return;
        }
        await ensureActiveGroup(session.session.activeOrganizationId);
        const myGroup = await getMyGroup();
        if (!myGroup) {
            setScreen({ type: "no-access" });
            return;
        }
        posthog.setPersonProperties({
            is_group_admin: myGroup.role === "admin" || myGroup.role === "owner",
        });
        if (myGroup.role === "admin" || myGroup.role === "owner") {
            window.history.pushState({}, "", "/admin");
            setScreen({ type: "group-admin-dashboard" });
            return;
        }
        onStartGame(session.user);
    }
    async function ensureActiveGroup(activeOrgId) {
        if (activeOrgId)
            return;
        const groups = await listGroups();
        if (groups.length === 0) {
            setScreen({ type: "no-access" });
            return;
        }
        await activateGroup(groups[0].id);
    }
    function handleLogout() {
        void signOut()
            .catch(() => { })
            .finally(() => {
            window.location.replace("/");
        });
    }
    async function handleInviteSuccess(registrationRole) {
        try {
            const session = await getSession();
            if (session) {
                await identifyUser(session.user);
                posthog.capture("account_registered", {
                    registration_role: registrationRole,
                });
            }
        }
        finally {
            window.location.replace("/");
        }
    }
    switch (screen.type) {
        case "loading":
            return (_jsx("div", { className: "min-h-screen bg-slate-50 flex items-center justify-center", children: _jsx("div", { className: "w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" }) }));
        case "already-logged-in":
            return (_jsx("div", { className: "min-h-screen bg-slate-50 flex items-center justify-center font-sans", children: _jsxs("div", { className: "bg-white border border-slate-200 rounded-xl p-8 w-[400px] shadow-sm", children: [_jsx("h2", { className: "text-base font-semibold text-slate-900 mb-2", children: "Bereits angemeldet" }), _jsxs("p", { className: "text-sm text-slate-500 mb-5", children: ["Du bist als ", _jsx("strong", { children: screen.userName }), " angemeldet. Ein Nutzer kann nur einer Gruppe angeh\u00F6ren. Um diesen Einladungslink zu verwenden, musst du dich zuerst abmelden und ein neues Konto erstellen."] }), _jsxs("div", { className: "flex gap-3", children: [_jsx("button", { onClick: handleLogout, className: "px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg cursor-pointer border-0 transition-colors", children: "Abmelden" }), _jsx("button", { onClick: () => window.history.back(), className: "px-4 py-2 text-slate-500 border border-slate-200 rounded-lg text-sm cursor-pointer bg-transparent hover:bg-slate-50 transition-colors", children: "Zur\u00FCck" })] })] }) }));
        case "login":
            return (_jsx(LoginScreen, { onSuccess: () => runBootstrap(), onForgotPassword: () => setScreen({ type: "forgot-password" }), onRegisterWithCode: () => setScreen({ type: "code-entry" }) }));
        case "code-entry":
            return (_jsx(CodeEntryScreen, { onCodeValid: (code) => setScreen({ type: "user-register", code }), onBack: () => setScreen({ type: "login" }) }));
        case "forgot-password":
            return _jsx(ForgotPasswordPage, { onBack: () => setScreen({ type: "login" }) });
        case "reset-password":
            return _jsx(ResetPasswordPage, { token: screen.token });
        case "admin-register":
            return (_jsx(AdminRegisterPage, { token: screen.token, onSuccess: () => handleInviteSuccess("group_admin") }));
        case "user-register":
            return (_jsx(UserRegisterPage, { code: screen.code, onSuccess: () => handleInviteSuccess("member"), onBack: () => setScreen({ type: "code-entry" }) }));
        case "sysadmin-register":
            return (_jsx(SysadminRegisterPage, { token: screen.token, onSuccess: () => handleInviteSuccess("sysadmin") }));
        case "sysadmin-dashboard":
            return _jsx(SysadminDashboard, { onViewGroupMap: onViewGroupMap });
        case "group-admin-dashboard":
            return _jsx(GroupAdminDashboard, {});
        case "no-access":
            return (_jsx("div", { className: "min-h-screen bg-slate-50 flex items-center justify-center font-sans", children: _jsxs("div", { className: "bg-white border border-slate-200 rounded-xl p-8 w-[360px] text-center shadow-sm", children: [_jsx("p", { className: "text-3xl mb-3", children: "\uD83D\uDD12" }), _jsx("h2", { className: "text-base font-semibold text-slate-900 mb-2", children: "Kein Zugang" }), _jsxs("p", { className: "text-sm text-slate-500", children: ["Du bist noch keiner Gruppe zugeordnet.", _jsx("br", {}), "Bitte wende dich an deinen Gruppenadministrator."] })] }) }));
        case "error":
            return (_jsx("div", { className: "min-h-screen bg-slate-50 flex items-center justify-center font-sans", children: _jsxs("div", { className: "bg-white border border-slate-200 rounded-xl p-8 w-[360px] text-center shadow-sm", children: [_jsx("p", { className: "text-3xl mb-3", children: "\u26A0\uFE0F" }), _jsx("h2", { className: "text-base font-semibold text-slate-900 mb-2", children: "Verbindung fehlgeschlagen" }), _jsx("p", { className: "text-sm text-slate-500 mb-5", children: "Die Anmeldung konnte nicht geladen werden. Bitte versuche es erneut." }), _jsx("button", { onClick: () => {
                                setScreen({ type: "loading" });
                                runBootstrap();
                            }, className: "px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg cursor-pointer border-0 transition-colors", children: "Erneut versuchen" })] }) }));
    }
}
